<template>
  <button
    :disabled="editorIsFullscreen && fullscreenSkip"
    class="board-navigation-overlay-page"
    :class="{
      'board-navigation-overlay-page--selected': page.id === currentPageId,
    }"
    :aria-label="ariaLabel"
    :tabindex="expandedAndVisible ? undefined : -1"
    @click="goToPageId(page.id)"
  >
    <!-- For full-slide assets, use the asset thumbnail -->
    <LoadingIndicator
      v-if="assetId && !asset"
      class="board-navigation-overlay-page__background"
      absolute
      transparent
    />
    <NebulaThumbnail
      v-else-if="showThumbnail"
      class="board-navigation-overlay-page__background"
      :asset-id="asset.id"
      image-size="small"
      :asset-type="asset.type && asset.type.name"
    />

    <!-- Otherwise show the slide thumbnail or a default -->
    <div
      v-else
      class="board-navigation-overlay-page__background"
      :class="{
        'board-navigation-overlay-page__background--default': !pageBackgroundUrl,
      }"
      :style="{
        'background-image': pageBackgroundUrl
          ? `url(${pageBackgroundUrl})`
          : undefined
      }"
    />
    <NebulaIcon
      v-if="editorIsFullscreen && fullscreenSkip"
      size="s"
      symbol-id="hide"
      class="board-navigation-overlay-page__hidden-icon"
    />
    <NebulaBadge
      class="board-navigation-overlay-page__number"
      size="s"
    >
      {{ page.index + 1 }}
    </NebulaBadge>
  </button>
</template>

<script>
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import {
  NebulaBadge,
  NebulaIcon,
  NebulaThumbnail,
} from '@discoveryedu/nebula-components';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import * as types from '@/lib/constants/store';
import { ASSET_TYPE_TEI } from '@/lib/constants';
import {
  useAssetStore,
  useEditorStore,
} from '@/stores';

export default {
  name: 'BoardNavigationOverlayPage',
  components: {
    NebulaIcon,
    NebulaBadge,
    NebulaThumbnail,
    LoadingIndicator,
  },
  props: {
    expandedAndVisible: {
      type: Boolean,
      required: true,
    },
    page: {
      type: Object,
      required: true,
    },
    sectionName: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useEditorStore, [
      'currentPageId',
      'editorIsFullscreen',
    ]),
    asset() {
      return this.assets[this.assetId];
    },
    assetId() {
      return this.page.options && this.page.options.asset_id;
    },
    fullscreenSkip() {
      return this.page.options && this.page.options.fullscreen_skip;
    },
    pageBackgroundUrl() {
      return get(this.page, 'thumbnail.url');
    },
    showThumbnail() {
      return this.asset && this.asset.type?.id !== ASSET_TYPE_TEI;
    },
    ariaLabel() {
      return this.sectionName
        ? this.$t('%(sectionName)s - Slide %(currentPage)s', {
          currentPage: this.page.index + 1,
          sectionName: this.sectionName,
        })
        : this.$t('Slide %(currentPage)s', { currentPage: this.page.index + 1 });
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_PAGE_BY_ID,
    ]),
    goToPageId(pageId) {
      this[types.VIEW_PAGE_BY_ID](pageId);
    },
  },
};
</script>

<style lang="stylus">
.board-navigation-overlay-page {
  background: transparent;
  border-radius: $nebula-border-radius-small;
  border-width: 0;
  flex: 102px 0 0;
  height: 64px;
  margin: 2px 0 $nebula-space-1x 0;
  overflow: hidden;
  position: relative;
  padding: 0;
  width: 102px;

  &:before {
    background: $nebula-color-platform-interface-1100;
    border-radius: $nebula-border-radius-small;
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
  }

  &--selected {
    .board-navigation-overlay-page__number .nebula-badge.nebula-badge--fill {
      background: $nebula-color-interactive-blue-300;
    }

    &:not(:focus) {
      border: 2px solid $nebula-color-white;
    }
  }

  &:disabled {
    .board-navigation-overlay-page__background {
      opacity: .65;
    }
  }

  &:focus {
    outline: $nebula-color-white solid 1px;
    outline: -webkit-focus-ring-color auto 1px;
  }

  &__number .nebula-badge.nebula-badge--fill {
    background: $nebula-color-platform-interface-1100;
    border-bottom-left-radius: 0;
    border-top-right-radius: 0;
    bottom: 0;
    position: absolute;
    right: 0;
    z-index: 4;
  }

  &__hidden-icon {
    left: "calc(50% - %s)" % $nebula-space-1x;
    position: absolute;
    top: "calc(50% - %s)" % $nebula-space-1x;
    z-index: 3;
  }

  &__background {
    background-repeat: no-repeat;
    background-size: 100%;
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;

    &.nebula-thumbnail {
      aspect-ratio: auto; // prevent letterboxing
    }

    &--default {
      background-image: url('../../assets/blank_board.png');
    }
  }
}
</style>
